import useEnvironmentStore from "@/store/useEnvironmentStore";
import { capitalize } from "lodash";
import pluralize from "pluralize";

type KeyWordsConversionMilken =
  | "employee"
  | "colleagues"
  | "department"
  | "interdepartmental"
  | "onboarding_select_label_department"
  | "onboarding_select_placeholder_department"
  | "follow_the_stars_point"
  | "job_title_label"
  | "job_title_placeholder"
  | "organization_label"
  | "organization_placeholder"
  | "insight_interdepartmental_title"
  | "confirmation_deletion_access_title"
  | "confirmation_deletion_access_body"
  | "last_name_label"
  | "last_name_placeholder"
  | "create_event_setting_toggle_label"
  | "create_event_setting_toggle_on_success"
  | "create_event_setting_toggle_off_success"
  | "create_circle_setting_toggle_label"
  | "create_circle_setting_toggle_on_success"
  | "create_circle_setting_toggle_off_success";

const BaseWords: Record<KeyWordsConversionMilken, string> = {
  employee: "employee",
  colleagues: "colleagues",
  department: "department",
  interdepartmental: "interdepartmental",
  onboarding_select_label_department: "Department/Group",
  onboarding_select_placeholder_department: "Select Department/Group",
  follow_the_stars_point: "It's Not an Ideal Match",
  job_title_label: "Job Title",
  job_title_placeholder: "Enter Your Job Title",
  organization_label: "Organization",
  organization_placeholder: "Enter Your Organization",
  insight_interdepartmental_title: "Interdepartmental",
  confirmation_deletion_access_title: "Confirm Deletion of Employee Access",
  confirmation_deletion_access_body:
    "Are you certain you want to remove this employee's access privileges?",
  last_name_label: "Last Name",
  last_name_placeholder: "Enter Last Name",
  create_event_setting_toggle_label: "Allow everyone to create an events",
  create_event_setting_toggle_on_success:
    "You have successfully allowed Everyone to create Events",
  create_event_setting_toggle_off_success:
    "You have successfully disallowed Everyone to create Events",
  create_circle_setting_toggle_label: "Allow everyone to create circles",
  create_circle_setting_toggle_on_success:
    "You have successfully allowed Everyone to create Circles",
  create_circle_setting_toggle_off_success:
    "You have successfully disallowed Everyone to create Circles",
};

const ConferenceWords: Record<KeyWordsConversionMilken, string> = {
  employee: "user",
  colleagues: "connection",
  department: "group",
  interdepartmental: "intergroup",
  onboarding_select_label_department: "Group",
  onboarding_select_placeholder_department: "Select Group",
  follow_the_stars_point: "Insufficient Data Points",
  job_title_label: "Job Title",
  job_title_placeholder: "Enter Job Title",
  organization_label: "Organization or University",
  organization_placeholder: "Enter Organization/University",
  insight_interdepartmental_title: "Inter-Group",
  confirmation_deletion_access_title: "Confirm Deletion of User Access",
  confirmation_deletion_access_body:
    "Are you certain you want to remove this user's access privileges?",
  last_name_label: "Last Name",
  last_name_placeholder: "Enter Last Name",
  create_event_setting_toggle_label: "Allow everyone to create an events",
  create_event_setting_toggle_on_success:
    "You have successfully allowed Everyone to create Events",
  create_event_setting_toggle_off_success:
    "You have successfully disallowed Everyone to create Events",
  create_circle_setting_toggle_label: "Allow everyone to create circles",
  create_circle_setting_toggle_on_success:
    "You have successfully allowed Everyone to create Circles",
  create_circle_setting_toggle_off_success:
    "You have successfully disallowed Everyone to create Circles",
};

const MilkenWords: Record<KeyWordsConversionMilken, string> = {
  employee: "user",
  colleagues: "connection",
  department: "group",
  interdepartmental: "intergroup",
  onboarding_select_label_department: "Group",
  onboarding_select_placeholder_department: "Select Group",
  follow_the_stars_point: "Insufficient Data Points",
  job_title_label: "Job Title or Student",
  job_title_placeholder: "Enter Job Title/Student",
  organization_label: "Organization or University",
  organization_placeholder: "Enter Organization/University",
  insight_interdepartmental_title: "Inter-Group",
  confirmation_deletion_access_title: "Confirm Deletion of User Access",
  confirmation_deletion_access_body:
    "Are you certain you want to remove this user's access privileges?",
  last_name_label: "Last Name & Class Year",
  last_name_placeholder: "Last Name & Class Year",
  create_event_setting_toggle_label: "Allow everyone to create an events",
  create_event_setting_toggle_on_success:
    "You have successfully allowed Everyone to create Events",
  create_event_setting_toggle_off_success:
    "You have successfully disallowed Everyone to create Events",
  create_circle_setting_toggle_label: "Allow everyone to create circles",
  create_circle_setting_toggle_on_success:
    "You have successfully allowed Everyone to create Circles",
  create_circle_setting_toggle_off_success:
    "You have successfully disallowed Everyone to create Circles",
};

const HbasWords: Record<KeyWordsConversionMilken, string> = {
  employee: "user",
  colleagues: "connection",
  department: "group",
  interdepartmental: "intergroup",
  onboarding_select_label_department: "Group",
  onboarding_select_placeholder_department: "Select Group",
  follow_the_stars_point: "Insufficient Data Points",
  job_title_label: "Job Title",
  job_title_placeholder: "Enter Your Job Title",
  organization_label: "Organization",
  organization_placeholder: "Enter Your Organization",
  insight_interdepartmental_title: "Inter-Group",
  confirmation_deletion_access_title: "Confirm Deletion of User Access",
  confirmation_deletion_access_body:
    "Are you certain you want to remove this user's access privileges?",
  last_name_label: "Last Name & Class Year",
  last_name_placeholder: "Last Name & Class Year",
  create_event_setting_toggle_label: "Allow everyone to create an events",
  create_event_setting_toggle_on_success:
    "You have successfully allowed Everyone to create Events",
  create_event_setting_toggle_off_success:
    "You have successfully disallowed Everyone to create Events",
  create_circle_setting_toggle_label: "Allow everyone to create circles",
  create_circle_setting_toggle_on_success:
    "You have successfully allowed Everyone to create Circles",
  create_circle_setting_toggle_off_success:
    "You have successfully disallowed Everyone to create Circles",
};

type WordsConfig = {
  capitalize?: boolean;
  plural?: boolean;
};

const useWordsConversionEnv = () => {
  const { env, module } = useEnvironmentStore();

  const convertWords = (
    key: KeyWordsConversionMilken,
    config?: WordsConfig,
  ) => {
    switch (env) {
      // for ML env
      case "ml":
        if (config?.capitalize)
          return pluralize(
            capitalize(MilkenWords[key]),
            config?.plural ? 2 : 1,
          );
        return pluralize(MilkenWords[key], config?.plural ? 2 : 1);

      // for HBAS env
      case "hbas":
        if (config?.capitalize)
          return pluralize(capitalize(HbasWords[key]), config?.plural ? 2 : 1);
        return pluralize(HbasWords[key], config?.plural ? 2 : 1);

      default:
        // for conferences module
        if (module === "conference") {
          if (config?.capitalize)
            return pluralize(
              capitalize(ConferenceWords[key]),
              config?.plural ? 2 : 1,
            );
          return pluralize(ConferenceWords[key], config?.plural ? 2 : 1);
        } else {
          // Default case for other environments/module
          if (config?.capitalize)
            return pluralize(
              capitalize(BaseWords[key]),
              config?.plural ? 2 : 1,
            );
          return pluralize(BaseWords[key], config?.plural ? 2 : 1);
        }
    }
  };

  return {
    convertWords,
  };
};

export default useWordsConversionEnv;
